import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, CreditCard, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

export function OutstandingExtensions({ festivalId, onPaid }) {
  const [items, setItems] = useState([]);
  const [modalFor, setModalFor] = useState(null);
  const [busy, setBusy] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = () => api.get(`/festival/${festivalId}/extensions/outstanding`)
    .then((r) => {
      const rows = r.data.items || [];
      setItems(rows);
      const pending = rows.find((e) => e.mode === "on_payment");
      if (pending && sessionStorage.getItem(`rl_ext_modal_${pending.id}`) !== "dismissed") setModalFor(pending);
    })
    .catch(() => setItems([]));

  useEffect(() => {
    const sid = params.get("ext_paid");
    if (!sid) { load(); return; }
    api.get(`/festival/extension-payment/${sid}`)
      .then((r) => { if (r.data.payment_status === "paid") { toast.success(`Payment received — festival now ends ${fmt(r.data.end_at)}`); onPaid?.(); } })
      .catch(() => {})
      .finally(() => { params.delete("ext_paid"); setParams(params, { replace: true }); load(); });
  }, [festivalId]); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = async (ext) => {
    setBusy(ext.id);
    try {
      const r = await api.post(`/festival/${festivalId}/extensions/${ext.id}/pay`, { origin_url: window.location.origin });
      window.location.assign(r.data.url);
    } catch (e) { toast.error(formatApiError(e, "Couldn't open payment")); setBusy(null); }
  };
  const dismiss = () => { sessionStorage.setItem(`rl_ext_modal_${modalFor.id}`, "dismissed"); setModalFor(null); };

  if (!items.length) return null;
  return (
    <>
      {items.map((e) => (
        <div key={e.id} className="mb-4 border border-[#FFB020] bg-[#1A140A] p-3 flex flex-wrap items-center justify-between gap-3" data-testid={`ext-outstanding-banner-${e.id}`}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-[#FFB020] shrink-0 mt-0.5" />
            <div className="text-sm text-white">
              {e.mode === "pay_later" ? "Payment outstanding for your festival extension" : "Your festival extension is ready — pay to activate it"}
              <div className="text-[11px] text-[#A3A3A3]">£{Number(e.amount).toFixed(2)} · {e.mode === "pay_later" ? "festival now ends" : "extends end to"} {fmt(e.new_end_at)}</div>
            </div>
          </div>
          <button type="button" onClick={() => pay(e)} disabled={busy === e.id} data-testid={`ext-outstanding-pay-${e.id}`}
            className="inline-flex items-center gap-1 px-4 py-2 text-[11px] uppercase tracking-widest font-bold bg-[#FFB020] text-black hover:bg-white disabled:opacity-50">
            <CreditCard className="w-3.5 h-3.5" /> {busy === e.id ? "Opening…" : "Pay Now"}
          </button>
        </div>
      ))}
      {modalFor && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 p-4" data-testid="ext-pay-modal" onClick={dismiss}>
          <div className="w-full max-w-md bg-[#0A0A0A] border-2 border-[#FFB020] p-6" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="label-mono text-[#FFB020] text-[10px] mb-1">FESTIVAL EXTENSION</div>
                <h2 className="h-display text-xl">Extension ready — £{Number(modalFor.amount).toFixed(2)}</h2>
              </div>
              <button type="button" onClick={dismiss} data-testid="ext-pay-modal-close" className="text-[#A3A3A3] hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-[#A3A3A3] mb-5">Pay now to extend your festival end to <span className="text-white">{fmt(modalFor.new_end_at)}</span>. It activates as soon as payment is confirmed.</p>
            <button type="button" onClick={() => pay(modalFor)} disabled={busy === modalFor.id} data-testid="ext-pay-modal-pay"
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 text-xs uppercase tracking-widest font-bold bg-[#CCFF00] text-black hover:bg-white disabled:opacity-50">
              <CreditCard className="w-4 h-4" /> {busy === modalFor.id ? "Opening secure checkout…" : `Pay £${Number(modalFor.amount).toFixed(2)} now`}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
